import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import orderService from "../../services/order.service";
import "./OrderDetailsPage.css";
import { generateInvoice } from "../../utils/generateInvoice";

const OrderDetailsPage = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCancelling, setIsCancelling] = useState(false);
  const [trackingActivities, setTrackingActivities] = useState([]);

  const [showFullTimeline, setShowFullTimeline] = useState(false);

  const fetchOrderDetails = useCallback(async () => {
    try {
      const data = await orderService.getOrderById(orderId);
      setOrder(data);

      if (data.tracking_number) {
        try {
          const courier = (data.carrier_name || "").toLowerCase();
          let activities = [];

          if (courier.includes("ekart")) {
            const targetUrl = encodeURIComponent(
              `https://app.elite.ekartlogistics.in/api/v1/track/${data.tracking_number}`,
            );
            const response = await fetch(`https://corsproxy.io/?${targetUrl}`);

            if (response.ok) {
              const trackData = await response.json();
              if (trackData?.track) {
                if (Array.isArray(trackData.track.details)) {
                  activities = [...trackData.track.details];
                }
                const latestScan = {
                  status:
                    trackData.track.status ||
                    trackData.track.desc ||
                    "Order Placed",
                  location: trackData.track.location || "Logistics Hub",
                  ctime: trackData.track.ctime,
                };
                activities.push(latestScan);
              }
            }
          } else {
            try {
              const trackData = await orderService.getOrderTracking(orderId);
              activities =
                trackData?.tracking_data?.shipment_track_activities ||
                trackData?.tracking_data?.scans ||
                [];
            } catch (err) {
              console.warn("No backend tracking setup for this courier.");
            }
          }

          setTrackingActivities([...activities].reverse());
        } catch (trackErr) {
          console.error("Could not load live tracking scans:", trackErr);
        }
      }
    } catch (error) {
      console.error("Failed to load order details:", error);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrderDetails();
  }, [fetchOrderDetails]);

  const handleCancelOrder = async () => {
    const confirmCancel = window.confirm(
      "Are you sure you want to cancel this order? This action cannot be undone.",
    );
    if (!confirmCancel) return;

    try {
      setIsCancelling(true);
      await orderService.cancelOrder(orderId);
      await fetchOrderDetails();
      alert("Order cancelled successfully.");
    } catch (error) {
      console.error("Error cancelling order:", error);
      alert(
        error.response?.data?.message ||
          "Failed to cancel order. Please try again.",
      );
    } finally {
      setIsCancelling(false);
    }
  };

  const getCorrectTrackingUrl = () => {
    if (!order?.tracking_number) return "#";
    const courier = (order.carrier_name || "").toLowerCase();

    if (courier.includes("ekart")) {
      return `https://ekartlogistics.com/shipmenttrack/${order.tracking_number}`;
    }
    if (courier.includes("shiprocket")) {
      return `https://shiprocket.co/tracking/${order.tracking_number}`;
    }
    return (
      order.tracking_url ||
      `https://ekartlogistics.com/shipmenttrack/${order.tracking_number}`
    );
  };

  if (loading) {
    return (
      <div className="order-details-page fade-in">
        <div className="order-wrapper skeleton-wrapper">
          <div className="skeleton-header"></div>
          <div className="skeleton-body"></div>
        </div>
      </div>
    );
  }

  if (!order) {
    return (
      <div className="order-details-page fade-in">
        <div className="order-wrapper empty-state">
          <h2>Order not found</h2>
          <button className="btn-back" onClick={() => navigate(-1)}>
            &larr; Go Back
          </button>
        </div>
      </div>
    );
  }

  const statusClass = order.status
    ? order.status.split(" ")[0].toLowerCase()
    : "placed";
  const canCancel =
    ["Pending Payment", "Paid", "Processing", "COD"].includes(order.status) &&
    !order.tracking_number;
  const visibleActivities = showFullTimeline
    ? trackingActivities
    : trackingActivities.slice(0, 3);

  return (
    <div className="order-details-page fade-in">
      <div className="order-wrapper">
        <button
          className="btn-back"
          onClick={() => navigate("/account?tab=orders")}
        >
          &larr; Back to Orders
        </button>

        <div className="order-top">
          <div className="order-title-group">
            <h1>
              Order <span>#{order.order_id}</span>
            </h1>
            <p className="order-date-subtitle">
              Placed on{" "}
              {new Date(order.createdAt).toLocaleDateString("en-IN", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}
            </p>
          </div>
          <div className={`status-pill ${statusClass}`}>{order.status}</div>
        </div>

        <div className="order-grid">
          {/* Left - Tracking Status */}
          <div className="detail-card tracking-card">
            <h3>Tracking Status</h3>

            {order.status === "Cancelled" ? (
              <div
                style={{
                  padding: "20px",
                  background: "#fee2e2",
                  color: "#991b1b",
                  borderRadius: "8px",
                  textAlign: "center",
                  border: "1px solid #f87171",
                }}
              >
                <div
                  style={{
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#dc2626",
                    marginBottom: "8px",
                  }}
                >
                  🚫 Order Cancelled
                </div>
                <p
                  style={{ margin: 0, fontSize: "0.95rem", lineHeight: "1.5" }}
                >
                  Your order has been successfully cancelled. <br />
                  <strong>If you paid online</strong>, your refund has been
                  automatically initiated and will reflect in your original
                  payment method within <strong>5-7 business days</strong>.
                </p>
              </div>
            ) : (
              <div className="progress-wrapper">
                <div className="progress-line"></div>
                <div
                  className="progress-fill"
                  style={{
                    width:
                      order.status === "Placed"
                        ? "0%"
                        : order.status === "Processing"
                          ? "33%"
                          : order.status === "Shipped"
                            ? "66%"
                            : order.status === "Delivered"
                              ? "100%"
                              : "0%",
                  }}
                ></div>
                {["Placed", "Processing", "Shipped", "Delivered"].map(
                  (step, index) => {
                    const statusMap = {
                      Placed: 0,
                      Processing: 1,
                      Shipped: 2,
                      Delivered: 3,
                    };
                    const current = statusMap[order.status] ?? 0;
                    return (
                      <div
                        key={index}
                        className={`progress-step ${index <= current ? "active" : ""}`}
                        style={{ left: `${(index / 3) * 100}%` }}
                      >
                        <div className="progress-dot">
                          {index <= current && (
                            <i className="fas fa-check">✓</i>
                          )}
                        </div>
                        <span>{step}</span>
                      </div>
                    );
                  },
                )}
              </div>
            )}

            {order.tracking_number && order.status !== "Cancelled" && (
              <>
                <div className="live-tracking-box">
                  <div className="tracking-info-text">
                    <p className="courier-name">
                      <span className="label">Courier:</span>{" "}
                      {order.carrier_name || "Ekart Logistics"}
                    </p>
                    <p className="awb-number">
                      <span className="label">Tracking ID:</span>{" "}
                      {order.tracking_number}
                    </p>
                  </div>
                  {order.tracking_number && (
                    <a
                      href={getCorrectTrackingUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-track-live"
                    >
                      Track Live ↗
                    </a>
                  )}
                </div>

                <div className="tracking-timeline-container">
                  <h4
                    className="timeline-title"
                    style={{ marginBottom: "12px", textTransform: "uppercase" }}
                  >
                    Shipment Updates
                  </h4>

                  {/* ✅ SCROLLABLE TIMELINE WRAPPER */}
                  <div
                    className={`timeline-scroll-area ${showFullTimeline ? "is-expanded" : ""}`}
                  >
                    <div className="vertical-timeline">
                      {trackingActivities.length > 0 ? (
                        visibleActivities.map((scan, index) => {
                          const activityStatus =
                            scan.activity ||
                            scan.status_name ||
                            scan.status ||
                            scan.desc ||
                            "Status Updated";
                          const locationStr =
                            scan.location || scan.city || "Logistics Hub";
                          const timeStamp =
                            scan.ctime ||
                            scan.date ||
                            scan.timestamp ||
                            scan.updated_at;

                          return (
                            <div className="timeline-item fade-in" key={index}>
                              <div
                                className={`timeline-dot ${index === 0 && !showFullTimeline ? "latest" : ""}`}
                              ></div>
                              <div className="timeline-content">
                                <p className="timeline-status">
                                  {activityStatus}
                                </p>
                                {locationStr &&
                                  locationStr !== "Logistics Hub" && (
                                    <p className="timeline-location">
                                      {locationStr}
                                    </p>
                                  )}
                                {timeStamp && (
                                  <p className="timeline-time">
                                    {new Date(timeStamp).toLocaleString(
                                      "en-IN",
                                      {
                                        month: "short",
                                        day: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                      },
                                    )}
                                  </p>
                                )}
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="timeline-item">
                          <div className="timeline-dot latest"></div>
                          <div className="timeline-content">
                            <p className="timeline-status">
                              Shipment Info Received
                            </p>
                            <p className="timeline-location">
                              Awaiting logistics pickup scan.
                            </p>
                            <p
                              className="timeline-time"
                              style={{ opacity: 0.6 }}
                            >
                              Pending Update...
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* ✅ TOGGLE BUTTON OUTSIDE SCROLL AREA */}
                  {trackingActivities.length > 3 && (
                    <button
                      className="btn-toggle-timeline"
                      onClick={() => setShowFullTimeline(!showFullTimeline)}
                    >
                      {showFullTimeline
                        ? "Hide Details ⬆"
                        : `See Full Tracking History (${trackingActivities.length} updates) ⬇`}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>

          {/* Right - Order Summary */}
          {/* ✅ ADDED STICKY WRAPPER */}
          <div className="sticky-summary">
            <div className="detail-card summary-card">
              <h3>Order Summary</h3>
              <div className="summary-content">
                <div className="summary-row">
                  <span className="label">Order Date</span>
                  <span className="value">
                    {new Date(order.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="label">Payment Status</span>
                  <span
                    className="value"
                    style={{
                      color:
                        order.status === "Cancelled"
                          ? "var(--text-secondary)"
                          : order.status === "Payment Failed"
                            ? "var(--danger-color, #ef4444)"
                            : order.status === "Pending Payment"
                              ? "#d97706" /* Orange for pending */
                              : "var(--success-color, #10b981)",
                      fontWeight: 600,
                    }}
                  >
                    {order.status === "COD"
                      ? "Cash on Delivery"
                      : order.status === "Cancelled"
                        ? "Refunded/Cancelled"
                        : order.status === "Payment Failed"
                          ? "Failed"
                          : order.status === "Pending Payment"
                            ? "Pending"
                            : "Success"}
                  </span>
                </div>
                <div className="summary-divider"></div>
                <div className="summary-row total">
                  <span>Total Amount</span>
                  <span className="total-price">
                    ₹{parseFloat(order.total_amount).toLocaleString("en-IN")}
                  </span>
                </div>

                {/* ✅ ONLY SHOW INVOICE BUTTON FOR CONFIRMED ORDERS */}
                {!["Pending Payment", "Payment Failed", "Cancelled"].includes(
                  order.status,
                ) && (
                  <button
                    onClick={() => generateInvoice(order)}
                    style={{
                      width: "100%",
                      marginTop: "16px",
                      padding: "10px",
                      background: "#f1f5f9",
                      color: "#0f172a",
                      border: "1px solid #cbd5e1",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "600",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    📄 Download Invoice
                  </button>
                )}

                {canCancel && (
                  <button
                    onClick={handleCancelOrder}
                    disabled={isCancelling}
                    style={{
                      width: "100%",
                      marginTop: "12px",
                      padding: "10px",
                      background: "#fee2e2",
                      color: "#dc2626",
                      border: "1px solid #f87171",
                      borderRadius: "6px",
                      cursor: isCancelling ? "not-allowed" : "pointer",
                      fontWeight: "600",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "8px",
                      opacity: isCancelling ? 0.7 : 1,
                    }}
                  >
                    {isCancelling ? "Cancelling..." : "🚫 Cancel Order"}
                  </button>
                )}

                <div className="summary-divider mt-4"></div>
                <div className="shipping-address-box">
                  <h4 className="box-title">Shipping To</h4>
                  <p className="ship-name">
                    <strong>{order.shipping_name}</strong>
                  </p>
                  <p>{order.shipping_line1}</p>
                  {order.shipping_line2 && <p>{order.shipping_line2}</p>}
                  <p>
                    {order.shipping_city}, {order.shipping_state}
                  </p>
                  <p>PIN: {order.shipping_pincode}</p>
                  <p>
                    Phone:{" "}
                    {order.shipping_phone || order.User?.phone_number || "N/A"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="items-section">
          <h3>Items in this Order ({order.OrderItems?.length || 0})</h3>
          <div className="items-list">
            {order.OrderItems?.map((item, index) => (
              <div key={index} className="item-card">
                <div className="item-img-container">
                  <img
                    src={item.Inventory?.image_url || "/fallback.png"}
                    alt={item.Inventory?.name || "Zynventics Product"}
                  />
                </div>
                <div className="item-details">
                  <h4>{item.Inventory?.name || "Zynventics Product"}</h4>
                  {item.Inventory?.sku && (
                    <p
                      className="item-sku"
                      style={{
                        fontSize: "0.85rem",
                        color: "var(--text-secondary)",
                        marginBottom: "4px",
                      }}
                    >
                      SKU: {item.Inventory.sku}
                    </p>
                  )}
                  <p className="item-qty">
                    Qty: <strong>{item.quantity}</strong>
                  </p>
                </div>
                <div className="item-price">
                  ₹{parseFloat(item.unit_price || 0).toLocaleString("en-IN")}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderDetailsPage;
